import { useEffect, useState } from "react";
import {
  usePersistedState,
  JSON_CODEC,
  readPersisted,
  defaultLocalStorage,
} from "./usePersistedState.js";
import {
  autoDisplayScale,
  isAutoDisplayScale,
  migrateLegacyDisplayScale,
  resolveDisplayScale,
} from "../lib/displayScale.js";

// Persisted display-size setting: "auto" (pick from the window width) or a scale. Stored under
// a v2 key because the original key was written on first load, so it cannot tell a chosen size
// from the old default; migrateLegacyDisplayScale carries real choices over.
const KEY = "sooth-ui-scale-v2";
const LEGACY_KEY = "sooth-ui-scale";

/** How long a resize must be still before Auto re-picks, so the UI never rescales mid-drag. */
const RESIZE_SETTLE_MS = 200;

/**
 * The window's OUTER size: in screen units that browser zoom (Cmd +/-) leaves alone, so Auto
 * follows a resize but never undoes a zoom.
 */
const readWindowSize = () => {
  try {
    return typeof window !== "undefined"
      ? { w: window.outerWidth, h: window.outerHeight }
      : { w: 0, h: 0 };
  } catch {
    return { w: 0, h: 0 };
  }
};

const pickAuto = (current) => {
  const { w, h } = readWindowSize();
  return autoDisplayScale(w, current, h);
};

/**
 * The display-size setting and the scale it resolves to.
 *
 * @returns {[number|string, (v: number|string) => void, number]} setting, setter, scale
 */
export function useDisplayScale() {
  const [fallback] = useState(() =>
    migrateLegacyDisplayScale(readPersisted(defaultLocalStorage, LEGACY_KEY, null, JSON_CODEC)),
  );
  const [setting, setSetting] = usePersistedState(KEY, fallback, JSON_CODEC);
  const [autoScale, setAutoScale] = useState(() => pickAuto());
  const auto = isAutoDisplayScale(setting);
  useEffect(() => {
    if (!auto) return undefined;
    let t = null;
    const update = () => {
      clearTimeout(t);
      t = setTimeout(() => setAutoScale((cur) => pickAuto(cur)), RESIZE_SETTLE_MS);
    };
    window.addEventListener("resize", update);
    // The window may have been resized while a fixed size was selected: re-pick on switching in.
    update();
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", update);
    };
  }, [auto]);
  return [setting, setSetting, resolveDisplayScale(setting, autoScale)];
}
